import { Injectable, OnDestroy, Renderer2 } from '@angular/core';
import { PdfDocumentManager, PdfImgCollector, PdfRangeCollector } from '../models/pdf-viewer';
import { BehaviorSubject } from 'rxjs';
import { PersonalizationsService } from '../../base/services/personalizations/personalizations.service';
import { Environment } from '../../../app/models/environment';
import { DocumentManager } from '../models/pdf-viewer/types';

@Injectable({
  providedIn: 'root',
})

export class PdfViewerService implements OnDestroy {
  private _pdfDocumentManager: PdfDocumentManager;
  private _pdfRangeCollector: PdfRangeCollector;
  private _pdfImageCollector: PdfImgCollector;
  private _environment: Environment;

  public pdfDocumentManagerList: DocumentManager[] = [];

  public indexIterator: number = 0;
  public hasFinishedRangeRender: BehaviorSubject<boolean>;

  constructor(private _customization: PersonalizationsService) {
    this._environment = this._customization.sendEnv;
  }

  get pdfDocumentManager () {
    return this._pdfDocumentManager;
  }

  set pdfDocumentManager (pdfDocumentManager: PdfDocumentManager) {
    this._pdfDocumentManager = pdfDocumentManager;
  }

  setCurrentPdfManager(pdfManager: PdfDocumentManager | undefined) {
    this.pdfDocumentManager = pdfManager;
  }

  getDocumentManagerById(documentId: string): PdfDocumentManager | undefined {
    if(this.pdfDocumentManagerList.length > 0) {
      return this.pdfDocumentManagerList.find((document) => document.documentId === documentId)?.documentManager;
    }

    return undefined;
  }

  async createPdfDocumentManager(documentId: string, renderer: Renderer2, pdfBuffer: Uint8Array | string) {
    this.pdfDocumentManager = this.getDocumentManagerById(documentId);

    if(this.pdfDocumentManager?.proxy) {
      return;
    }

    this.pdfDocumentManager = new PdfDocumentManager(this._environment, renderer, pdfBuffer);

    try {
      const proxy = await this.pdfDocumentManager.getDocumentProxy();
      if (!proxy) {
        throw new Error('PDF proxy could not be created');
      }
      this.pdfDocumentManager.documentId = documentId;
      this.pdfDocumentManagerList.push({documentId, documentManager: this.pdfDocumentManager});
    } catch (error) {
      throw new Error('Error while creating the pdf viewer manager');
    }
  }

  validateNewRange() {
    const ranges = this._pdfRangeCollector.getRanges();
    if (ranges.length > 0) {
      const currentRange = this._pdfRangeCollector.getCurrent();
      this.handlePagesOperation(currentRange);
    }
  }

  startRangeValidation(range: number[]): void {
    const totalPages = this.pdfDocumentManager?.proxy?.numPages;
    if (!totalPages) {
      throw new Error('PDF proxy is not ready yet');
    }

    this.createPagesCollection(totalPages, range);
    this.createRenderStateSubscription();
    this.validateNewRange();
  }

  startSingleOperations(pages: number[]): void {
    if (!this.hasFinishedRangeRender) {
      this.createRenderStateSubscription();
    }

    this.handlePagesOperation(pages);
  }

  handleOperationsByConfig(initialRange: number[] = [1]) {
    const isSinglePageConfig = this._environment?.features.singlePage;

    if (isSinglePageConfig) {
      this.startSingleOperations(initialRange);
      return;
    }

    this.startRangeValidation(initialRange);
  }

  createRenderStateSubscription() {
    this._pdfImageCollector = new PdfImgCollector();
    this.hasFinishedRangeRender = this._pdfImageCollector.hasFinishedListener();
    this.hasFinishedRangeRender.subscribe((hasFinished: boolean) => {
      const hasNextRange = this._pdfRangeCollector?.hasNextRange();

      if (hasFinished && hasNextRange) {
        this._pdfRangeCollector.setNext();
        this.validateNewRange();
      }
    });
  }

  createPagesCollection(pages: number, initialRange?: number[]) {
    this._pdfRangeCollector = new PdfRangeCollector();
    if (initialRange) {
      this._pdfRangeCollector.add(initialRange);
    }
    this._pdfRangeCollector.createRanges(pages);
  }

  async handlePagesOperation(range: number[]): Promise<void> {
    const promisesArray = this.pdfDocumentManager.getPageManagerOperations(range, this.addImageToCache.bind(this));
    await Promise.allSettled(promisesArray);
    this._pdfImageCollector.validate();
  }

  addImageToCache(img: HTMLImageElement) {
    const isSinglePageConfig = this._environment?.features.singlePage;

    if (!isSinglePageConfig) {
      this._pdfImageCollector.add(img);
      return;
    }

    const hasTheNode = this._pdfImageCollector.imagesCache.some((node) => node.id === img.id);
    if (!hasTheNode) {
      this._pdfImageCollector.add(img);
    }
  }

  ngOnDestroy(): void {
    if (this.hasFinishedRangeRender) {
      this.hasFinishedRangeRender.unsubscribe();
    }
  }
}
